import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";

const API_BASE_URL = "https://api.itrustsecure.com";

export default function InvoicePayPage({ user }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState("choose");
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [dropinReady, setDropinReady] = useState(false);
  const dropinInstanceRef = useRef(null);
  const containerRef = useRef(null);
  const [paying, setPaying] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE_URL}/invoices/${slug}`)
      .then(r => r.json())
      .then(d => { setInvoice(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [slug]);

  const initDropin = useCallback(async () => {
    if (step !== "pay") return;
    if (dropinInstanceRef.current) return;
    try {
      const dropin = await import("braintree-web-drop-in");
      const res = await fetch(`${API_BASE_URL}/invoices/${slug}/token`, { method: "POST" });
      if (!res.ok) { setError("Could not load payment form. Please try again."); return; }
      const { clientToken } = await res.json();
      if (!clientToken) { setError("Could not load payment form. Please try again."); return; }
      await new Promise(r => setTimeout(r, 100));
      const container = document.getElementById("dropin-container");
      if (!container) { setError("Payment form container not found."); return; }
      const instance = await dropin.default.create({
        authorization: clientToken,
        container: "#dropin-container",
      });
      dropinInstanceRef.current = instance;
      setDropinReady(true);
    } catch (err) {
      console.error("Dropin init error:", err);
      setError("Could not load payment form. Please refresh and try again.");
    }
  }, [step, slug]);

  useEffect(() => {
    if (step !== "pay") return;
    setDropinReady(false);
    setError("");
    const timer = setTimeout(() => { initDropin(); }, 200);
    return () => {
      clearTimeout(timer);
      if (dropinInstanceRef.current) {
        dropinInstanceRef.current.teardown().catch(() => {});
        dropinInstanceRef.current = null;
        setDropinReady(false);
      }
    };
  }, [step]);

  async function handlePay() {
    if (!dropinInstanceRef.current) { setError("Payment form not ready. Please wait a moment."); return; }
    setPaying(true);
    setError("");
    try {
      const { nonce } = await dropinInstanceRef.current.requestPaymentMethod();
      const body = user
        ? { nonce, buyerId: user.id }
        : { nonce, guestName, guestEmail };
      const res = await fetch(`${API_BASE_URL}/invoices/${slug}/pay`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (res.ok) setDone(data);
      else setError(data.error || "Payment failed. Please try again.");
    } catch (err) {
      setError("Payment failed. Please try again.");
    } finally { setPaying(false); }
  }

  function handleGuestContinue() {
    if (!guestName.trim()) { setError("Full name is required"); return; }
    if (!guestEmail.trim() || !guestEmail.includes("@")) { setError("Valid email is required"); return; }
    setError("");
    setStep("pay");
  }

  function copyLink() {
    navigator.clipboard.writeText(`https://www.itrustsecure.com/invoice/${slug}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  const platformFee = invoice ? (Number(invoice.amount) * 0.025) : 0;
  const total = invoice ? (Number(invoice.amount) + platformFee) : 0;
  const isSeller = user && invoice && user.id === invoice.sellerId;

  const s = {
    root: { fontFamily: "'Segoe UI', Arial, sans-serif", background: "#0a0f1e", color: "#f0f4ff", minHeight: "100vh", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 16px" },
    card: { background: "#0d1526", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 24, padding: 32, width: "100%", maxWidth: 460 },
    logo: { fontSize: 22, fontWeight: 800, color: "#fff", marginBottom: 28, textAlign: "center" },
    logoAccent: { color: "#3b82f6" },
    invoiceBox: { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 14, padding: 18, marginBottom: 24 },
    invoiceLabel: { fontSize: 11, color: "rgba(255,255,255,0.4)", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 },
    invoiceTitleText: { fontSize: 19, fontWeight: 700, marginBottom: 14 },
    invoiceDesc: { fontSize: 13, color: "rgba(255,255,255,0.5)", marginBottom: 14 },
    row: { display: "flex", justifyContent: "space-between", fontSize: 13, color: "rgba(255,255,255,0.6)", marginBottom: 6 },
    totalRow: { display: "flex", justifyContent: "space-between", fontSize: 18, fontWeight: 800, paddingTop: 10, borderTop: "1px solid rgba(255,255,255,0.08)", marginTop: 4 },
    sellerRow: { display: "flex", alignItems: "center", gap: 10, marginBottom: 16 },
    sellerAvatar: { width: 34, height: 34, borderRadius: "50%", background: "rgba(59,130,246,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, color: "#3b82f6", fontSize: 15, flexShrink: 0 },
    sellerName: { fontSize: 14, color: "rgba(255,255,255,0.7)", fontWeight: 600 },
    btn: (color) => ({ width: "100%", background: color, color: "#fff", border: "none", padding: "14px", borderRadius: 12, cursor: "pointer", fontWeight: 700, fontSize: 15, marginBottom: 10 }),
    outlineBtn: { width: "100%", background: "transparent", color: "rgba(255,255,255,0.8)", border: "1px solid rgba(255,255,255,0.25)", padding: "14px", borderRadius: 12, cursor: "pointer", fontWeight: 600, fontSize: 15, marginBottom: 10 },
    label: { fontSize: 13, fontWeight: 600, color: "rgba(255,255,255,0.7)", marginBottom: 6, display: "block" },
    input: { width: "100%", padding: "12px 14px", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 10, color: "#fff", fontSize: 14, marginBottom: 14, boxSizing: "border-box", outline: "none" },
    backBtn: { background: "transparent", border: "none", color: "rgba(255,255,255,0.5)", cursor: "pointer", fontSize: 13, marginBottom: 16, padding: 0 },
    protectionBox: { display: "flex", gap: 10, background: "rgba(22,163,74,0.08)", border: "1px solid rgba(22,163,74,0.2)", borderRadius: 10, padding: "10px 14px", marginTop: 10, marginBottom: 4 },
    protectionText: { fontSize: 12, color: "#4ade80", lineHeight: 1.5 },
    error: { background: "rgba(220,38,38,0.1)", border: "1px solid rgba(220,38,38,0.3)", borderRadius: 10, padding: "10px 14px", color: "#fca5a5", fontSize: 13, marginBottom: 14 },
    shareBox: { background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: 12, padding: 16, marginBottom: 16 },
    divider: { display: "flex", alignItems: "center", gap: 10, margin: "6px 0 14px", color: "rgba(255,255,255,0.3)", fontSize: 12 },
    dividerLine: { flex: 1, height: 1, background: "rgba(255,255,255,0.08)" },
    payingAs: { background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.15)", borderRadius: 10, padding: "10px 14px", fontSize: 13, color: "#60a5fa", marginBottom: 16 },
  };

  if (loading) return (
    <div style={s.root}>
      <div style={{ color: "rgba(255,255,255,0.5)", marginTop: 80 }}>Loading invoice...</div>
    </div>
  );

  if (!invoice || invoice.error) return (
    <div style={s.root}>
      <div style={s.card}>
        <div style={s.logo}>i<span style={s.logoAccent}>Trust Secure</span></div>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>❌</div>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Invoice not found</div>
          <div style={{ color: "rgba(255,255,255,0.5)" }}>This payment link may have expired or been canceled.</div>
        </div>
      </div>
    </div>
  );

  if (invoice.status === "PAID") return (
    <div style={s.root}>
      <div style={s.card}>
        <div style={s.logo}>i<span style={s.logoAccent}>Trust Secure</span></div>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Already Paid</div>
          <div style={{ color: "rgba(255,255,255,0.5)" }}>This invoice has already been paid.</div>
        </div>
      </div>
    </div>
  );

  if (invoice.status === "CANCELED") return (
    <div style={s.root}>
      <div style={s.card}>
        <div style={s.logo}>i<span style={s.logoAccent}>Trust Secure</span></div>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🚫</div>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Invoice Canceled</div>
          <div style={{ color: "rgba(255,255,255,0.5)" }}>This payment link has been canceled by the seller.</div>
        </div>
      </div>
    </div>
  );

  if (done) return (
    <div style={s.root}>
      <div style={s.card}>
        <div style={s.logo}>i<span style={s.logoAccent}>Trust Secure</span></div>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>🎉</div>
          <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 8 }}>Payment Successful!</div>
          <div style={{ color: "rgba(255,255,255,0.5)", marginBottom: 24 }}>
            {!user ? "Check your email for a confirmation and tracking link." : "Your order has been placed successfully."}
          </div>
          <div style={{ background: "rgba(59,130,246,0.1)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: 12, padding: 16, marginBottom: 20 }}>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", marginBottom: 8 }}>Order tracking link</div>
            <a href={done.trackingUrl} style={{ color: "#3b82f6", fontSize: 13, fontWeight: 600, wordBreak: "break-all" }}>{done.trackingUrl}</a>
          </div>
          <a href={done.trackingUrl} style={{ display: "block", background: "#3b82f6", color: "#fff", padding: "14px", borderRadius: 12, fontWeight: 700, fontSize: 15, textDecoration: "none", marginBottom: 10 }}>
            Track My Order →
          </a>
          {user && <button style={s.outlineBtn} onClick={() => navigate("/orders")}>View in My Orders</button>}
        </div>
      </div>
    </div>
  );

  const InvoiceSummary = () => (
    <div style={s.invoiceBox}>
      <div style={s.invoiceLabel}>Payment Request from</div>
      <div style={s.sellerRow}>
        <div style={s.sellerAvatar}>{invoice.seller?.fullName?.charAt(0)}</div>
        <div style={s.sellerName}>{invoice.seller?.fullName}</div>
      </div>
      <div style={s.invoiceTitleText}>{invoice.title}</div>
      {invoice.description && <div style={s.invoiceDesc}>{invoice.description}</div>}
      <div style={s.row}><span>Amount</span><span>${Number(invoice.amount).toFixed(2)}</span></div>
      <div style={s.row}><span>Platform fee (2.5%)</span><span>${platformFee.toFixed(2)}</span></div>
      <div style={s.totalRow}><span>Total</span><span style={{ color: "#3b82f6" }}>${total.toFixed(2)}</span></div>
    </div>
  );

  return (
    <div style={s.root}>
      <div style={s.card}>
        <div style={s.logo}>i<span style={s.logoAccent}>Trust Secure</span></div>

        <InvoiceSummary />

        {/* Seller: show share screen */}
        {isSeller && (
          <>
            <div style={{ background: "rgba(22,163,74,0.08)", border: "1px solid rgba(22,163,74,0.2)", borderRadius: 12, padding: "12px 16px", marginBottom: 16, fontSize: 13, color: "#4ade80", textAlign: "center" }}>
              ✅ Your invoice is live — share the link below with your buyer
            </div>
            <div style={s.shareBox}>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", marginBottom: 8 }}>Payment link:</div>
              <div style={{ fontSize: 12, color: "#3b82f6", wordBreak: "break-all", marginBottom: 12, fontFamily: "monospace" }}>
                https://www.itrustsecure.com/invoice/{slug}
              </div>
              <button style={s.btn("#3b82f6")} onClick={copyLink}>
                {copied ? "✅ Copied!" : "📋 Copy Payment Link"}
              </button>
            </div>
            <div style={{ background: "rgba(59,130,246,0.06)", border: "1px solid rgba(59,130,246,0.15)", borderRadius: 12, padding: "12px 16px", marginBottom: 12, fontSize: 13, color: "rgba(255,255,255,0.5)", textAlign: "center" }}>
              💡 When your buyer opens this link, they'll see a checkout page with options to pay as guest, sign in, or create an account.
            </div>
            <button style={s.outlineBtn} onClick={() => navigate("/invoices")}>← Back to Invoices</button>
          </>
        )}

        {/* Choose step */}
        {!isSeller && step === "choose" && (
          <>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 16, textAlign: "center" }}>How would you like to pay?</div>
            {user ? (
              <>
                <button style={s.btn("#3b82f6")} onClick={() => setStep("pay")}>✅ Pay as {user.fullName}</button>
                <div style={s.divider}><div style={s.dividerLine} /><span>or</span><div style={s.dividerLine} /></div>
                <button style={s.outlineBtn} onClick={() => setStep("guestInfo")}>👤 Pay as Guest Instead</button>
              </>
            ) : (
              <>
                <button style={s.btn("#3b82f6")} onClick={() => setStep("guestInfo")}>👤 Pay as Guest</button>
                <div style={s.divider}><div style={s.dividerLine} /><span>or</span><div style={s.dividerLine} /></div>
                <button style={s.outlineBtn} onClick={() => navigate(`/login?redirect=/invoice/${slug}`)}>🔑 Sign In to Pay</button>
                <button style={s.outlineBtn} onClick={() => navigate(`/login?redirect=/invoice/${slug}`)}>✨ Create Account & Pay</button>
              </>
            )}
            <div style={s.protectionBox}>
              <span>🔒</span>
              <div style={s.protectionText}><strong style={{ color: "#4ade80" }}>Buyer Protected</strong> — Payment held in escrow. Seller only receives funds after you confirm delivery.</div>
            </div>
          </>
        )}

        {/* Guest info step */}
        {!isSeller && step === "guestInfo" && (
          <>
            <button style={s.backBtn} onClick={() => { setStep("choose"); setError(""); }}>← Back</button>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Your details</div>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,0.4)", marginBottom: 16 }}>We'll send your order updates and tracking link here</div>
            <label style={s.label}>Full Name *</label>
            <input style={s.input} placeholder="Your full name" value={guestName} onChange={e => setGuestName(e.target.value)} onKeyDown={e => e.key === "Enter" && handleGuestContinue()} />
            <label style={s.label}>Email Address *</label>
            <input style={s.input} type="email" placeholder="your@email.com" value={guestEmail} onChange={e => setGuestEmail(e.target.value)} onKeyDown={e => e.key === "Enter" && handleGuestContinue()} />
            {error && <div style={s.error}>{error}</div>}
            <button style={s.btn("#3b82f6")} onClick={handleGuestContinue}>Continue to Payment →</button>
          </>
        )}

        {/* Payment step */}
        {!isSeller && step === "pay" && (
          <>
            <button style={s.backBtn} onClick={() => { setStep(user && !guestName ? "choose" : "guestInfo"); setError(""); }}>← Back</button>
            {!user && guestName && (
              <div style={s.payingAs}>Paying as <strong>{guestName}</strong> · {guestEmail}</div>
            )}
            {user && !guestName && (
              <div style={s.payingAs}>Paying as <strong>{user.fullName}</strong> · {user.email}</div>
            )}
            {error && <div style={s.error}>{error}</div>}
            <div id="dropin-container" ref={containerRef} style={{ marginBottom: 16, minHeight: 100 }} />
            {!dropinReady && !error && (
              <div style={{ textAlign: "center", color: "rgba(255,255,255,0.4)", fontSize: 13, marginBottom: 16 }}>⏳ Loading secure payment form...</div>
            )}
            <button
              style={{ ...s.btn("#16a34a"), opacity: (!dropinReady || paying) ? 0.5 : 1 }}
              onClick={handlePay}
              disabled={!dropinReady || paying}
            >
              {paying ? "Processing..." : `🔒 Pay $${total.toFixed(2)} Securely`}
            </button>
            <div style={s.protectionBox}>
              <span>🔒</span>
              <div style={s.protectionText}><strong style={{ color: "#4ade80" }}>Buyer Protected</strong> — Payment held in escrow until you confirm delivery.</div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}